import includes from 'lodash/includes';
import {OperationResult} from '../models/OperationResult';
import {ISmsSettings} from '../models/AccountSettings';
import {ICallback, IPromptCallback, ISmsData, IWhatsAppTemplateData} from './ts-sms/ts-sms.settings';
import {ISmsFlowLogger} from '../states/invite-new/invite.flow.logger';
import {SendFullSmsFlowService} from './ts-sms/SendFullSmsFlowService';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {SmsStatusParams} from './Session/SessionContracts';
import {ISmsService as ISmsServiceInfra} from '@techsee/techsee-client-infra/lib/services/smsService';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {IClientAsDashboardSMSParams, IVisualJourneyParams} from '../app.contracts';
import {AddressTypesEnum} from '@techsee/techsee-common/lib/constants/room.constants';

export interface IResponseSms {
    success: boolean;
    clientConnected?: boolean;
    isWhatsApp?: boolean;
}

export interface ISmsService {
    sendSms(
        smsData: ISmsData,
        progressCb: ICallback,
        allowPrompt?: boolean,
        promptCb?: IPromptCallback,
        addressType?: AddressTypesEnum
    ): Promise<IResponseSms>;

    abortSendSms(): void;

    sendWhatsAppTemplateMessage(whatsAppData: IWhatsAppTemplateData): Promise<any>;

    validatePhoneNumber(countryCode: string, phoneNumber: string): PromiseLike<OperationResult>;

    sendVisualJourney(data: IVisualJourneyParams): Promise<any>;

    sendClientAsDashboard(data: IClientAsDashboardSMSParams): Promise<any>;

    sendObserverInvitation(data: IClientAsDashboardSMSParams): Promise<any>;

    sendWarmTransferClientLink(data: {roomId: string}): Promise<any>;

    smsStatusChanged(data: SmsStatusParams, progressCb: ICallback): void;

    restoreSms(data: any, progressCb: ICallback): Promise<any>;
}

export enum ValidationResultCodes {
    SUCCESS = 'SUCCESS',
    LANDLINE = 'LANDLINE',
    UNREACHABLE = 'UNREACHABLE',
    INVALIDNUMBER = 'INVALIDNUMBER',
    UNEXPECTED = 'UNEXPECTED',
    TIMEOUT = 'TIMEOUT'
}

export class SmsService implements ISmsService {
    private _smsService: ISmsServiceInfra;

    private _roomService: IRoomService;

    private _smsSettings: ISmsSettings;

    private _smsEventLogs: ISmsFlowLogger;

    private _sendSmsFlow?: SendFullSmsFlowService | null;

    private allowSendByWhatsApp: boolean;

    private _browserUtilsService: IBrowserUtilsService;

    ValidationResultCodes = ValidationResultCodes;

    constructor(
        smsService: ISmsServiceInfra,
        roomService: IRoomService,
        smsSettings: ISmsSettings,
        smsEventLogs: ISmsFlowLogger,
        browserUtilsService: IBrowserUtilsService,
        allowSendByWhatsApp: boolean = false
    ) {
        this._smsService = smsService;
        this._roomService = roomService;
        this._smsSettings = smsSettings;
        this._smsEventLogs = smsEventLogs;
        this._browserUtilsService = browserUtilsService;
        this.allowSendByWhatsApp = allowSendByWhatsApp;
    }

    sendWhatsAppTemplateMessage(whatsAppData: IWhatsAppTemplateData): Promise<any> {
        // @ts-ignore
        return this._sendSmsFlow.sendWhatsAppTemplateMessage(whatsAppData);
    }

    sendSms(
        smsData: ISmsData,
        progressCb: ICallback,
        allowPrompt: boolean,
        promptCb?: IPromptCallback,
        addressType?: AddressTypesEnum
    ): Promise<IResponseSms> {
        if (this._sendSmsFlow !== null) {
            this.abortSendSms();
        }

        this._sendSmsFlow = new SendFullSmsFlowService(
            this._smsService,
            this._roomService,
            this._smsSettings,
            this._smsEventLogs,
            this._browserUtilsService
        );

        const shouldSendViaWhatsApp = this.allowSendByWhatsApp && addressType === AddressTypesEnum.WHATSAPP;

        return this._sendSmsFlow
            .sendSMS(smsData, progressCb, allowPrompt, promptCb, shouldSendViaWhatsApp)
            .catch((error) => {
                throw {...error, success: false};
            })
            .then((result: any) => {
                this.abortSendSms();

                return result;
            });
    }

    smsStatusChanged(data: SmsStatusParams, progressCb: ICallback): void {
        this._sendSmsFlow?.smsStatusChanged(data, progressCb);
    }

    restoreSms(data: any, progressCb: ICallback): any {
        if (!this._sendSmsFlow) {
            this._sendSmsFlow = new SendFullSmsFlowService(
                this._smsService,
                this._roomService,
                this._smsSettings,
                this._smsEventLogs,
                this._browserUtilsService
            );
        }
        this._sendSmsFlow?.restoreSms(data, progressCb);
    }

    sendVisualJourney(data: IVisualJourneyParams) {
        return this._smsService.sendVisualJourneySMS(data);
    }

    sendWarmTransferClientLink(data: {roomId: string}) {
        return this._smsService.sendWarmTransferClientLinkSMS(data);
    }

    sendClientAsDashboard(data: IClientAsDashboardSMSParams) {
        return this._smsService.sendClientAsDashboardSMS(data);
    }

    sendObserverInvitation(data: IClientAsDashboardSMSParams) {
        return this._smsService.sendObserverInvitationSMS(data);
    }

    abortSendSms(): void {
        this._sendSmsFlow && this._sendSmsFlow.dispose();
        this._sendSmsFlow = null;
    }

    validatePhoneNumber(countryCode: string, phoneNumber: string): PromiseLike<OperationResult> {
        this._smsEventLogs.phoneNumberValidationStarted(phoneNumber);

        return new Promise((resolve) => {
            const isTimeoutEnabled = true;
            const validationTimeout = 15 * 1000;
            let isTimedOut = false;

            let timeOutPtr = isTimeoutEnabled
                ? setTimeout(() => {
                      isTimedOut = true;
                      resolve(OperationResult.getSuccessResult());
                  }, validationTimeout)
                : null;

            const resetTimeout = () => {
                timeOutPtr && clearInterval(timeOutPtr);
                timeOutPtr = null;
            };

            this._smsService
                .validatePhoneNumber(countryCode, phoneNumber, this._smsSettings.smsGatewayConfig.primary)
                .then((response: any) => {
                    resetTimeout();

                    if (!isTimedOut) {
                        const result = response?.data || response;
                        const failedResults = [
                            this.ValidationResultCodes.INVALIDNUMBER,
                            this.ValidationResultCodes.LANDLINE,
                            this.ValidationResultCodes.UNREACHABLE
                        ];

                        if (result && result.code && includes(failedResults, result.code)) {
                            this._smsEventLogs.phoneNumberValidationFailed({result});

                            resolve(OperationResult.getFailResult(result.code, result));

                            return;
                        }
                        this._smsEventLogs.phoneNumberValidatedSuccessfully(phoneNumber);

                        resolve(OperationResult.getSuccessResult());
                    }
                })
                .catch((err: any) => {
                    this._smsEventLogs.phoneNumberValidatedUnexpectedError({err, phoneNumber});

                    resetTimeout();

                    if (!isTimedOut) {
                        resolve(OperationResult.getSuccessResult());
                    }
                });
        });
    }
}
